// #v-ifdef IS_PROD='true'
console.info(`🎯 ${APP_NAME} ${APP_VERSION} ${APP_MODE}`);
// #v-else
import { Cheats } from './utils/cheats';
// #v-endif
import '@esotericsoftware/spine-pixi-v8';
import { getUrlParam, Preloader } from '@magic/utils';
import { type AssetsManifest, Application as Pixi, Rectangle } from 'pixi.js';
import { MainController } from './controllers/Main.controller';
import { sounds } from '@pixijs-userland/spine-layout';
import { settingsStore, waitForSettingsHydration } from './stores/settings.store';
import { API } from './controllers/api/API';
import { setGameState, apiStore } from './stores/api.store';
import { DemoAPI } from './controllers/api/demoAPI/DemoAPIController';
import type { APIController } from './utils/api.types';
import { findSpineBundles } from './utils/spineBundles';
import { type GameConfig, setGameConfig } from './config/game.config';
import type { DemoConfig } from './config/demo.config';

declare global {
    interface Window {
        setLoadingProgress?: (progress: number) => void;
    }
}

export class MagicSlot {
    mainController!: MainController;
    pixi = new Pixi();
    preloader!: Preloader;
    api!: APIController;

    private demo?: DemoConfig;

    constructor(config: GameConfig, demo?: DemoConfig) {
        setGameConfig(config);

        this.demo = demo;

        window.addEventListener('DOMContentLoaded', () => this.init());
    }

    private async init() {
        const manifest = await fetch(`assets/assetsManifest.json`).then((r) => r.json());

        this.preloader = new Preloader(manifest, `assets`);

        await this.preloader.load(
            [`default`, `fonts`, `sprites`, ...findSpineBundles(manifest).names],
            window.setLoadingProgress,
        );

        await this.initPixi();

        // TODO: move this to only work in dev and remove from prod bundle
        const isDemo = !getUrlParam('sessionID') || getUrlParam('sessionID') === 'demo';

        this.api = isDemo ? new DemoAPI(this.demoConfig()) : new API();
        new Cheats(this);

        // #v-ifdef IS_PROD!='true'
        //  const isDemo = !getUrlParam('sessionID') || getUrlParam('sessionID') === 'demo';

        // this.api = isDemo ? new DemoAPI(this.demoConfig()) : new API();
        // new Cheats(this);
        // #v-else
        // this.api = new API();

        // #v-endif

        await waitForSettingsHydration();

        const wsDebug = settingsStore.getState().wsDebug;
        const connectionResponce = await this.api.connect(wsDebug);

        this.initGame(manifest);

        if (connectionResponce) {
            apiStore.getState().actions.setAuthenticateResponse(connectionResponce);
            setGameState('idle');
        }
    }

    private initGame(manifest: AssetsManifest) {
        document.getElementById('loading-animation')?.remove();

        this.mainController = new MainController(manifest, this.pixi.stage, this.api);

        window.addEventListener('resize', () => this.onResize());
        this.onResize();
        this.initSounds();
    }

    /** The game's demo data, or a clear error if it tried to run in demo mode without any. */
    private demoConfig(): DemoConfig {
        if (!this.demo) {
            throw new Error(
                'Demo mode needs demo data — pass it as the second argument to the MagicSlot constructor.',
            );
        }

        return this.demo;
    }

    private async initPixi() {
        await this.pixi.init({
            resizeTo: window,
            autoDensity: true,
            antialias: true,
            hello: true,
            backgroundAlpha: 0,
            resolution: window.devicePixelRatio || 2,
        });

        document.getElementById('pixi-container')!.appendChild(this.pixi.canvas);
    }

    private onResize = () => {
        this.mainController?.resize();

        this.pixi.stage.hitArea = new Rectangle(0, 0, window.innerWidth, window.innerHeight);
    };

    private async initSounds() {
        const { getState, subscribe } = settingsStore;
        const muted = getState().muted;

        subscribe(
            (state) => state.muted,
            (muted) => {
                sounds.updateSettings({
                    muted,
                });
            },
        );

        await sounds.init(this.preloader.manifest, {
            debug: settingsStore.getState().soundDebug,
            musicMuted: false,
            fxMuted: false,
            muted,
            musicVolume: 0.2,
            fxVolume: 0.5,
            soundsVolumes: {
                lose: 0.1,
                'lose-loop': 0.1,
            },
        });
    }
}
